/**
 * SignedOutScreen: the branded page a web app shows after sign-out (Jean,
 * 30 Oct 2026). Same layout and marks as SignInScreen; it never starts a
 * sign-in redirect by itself. "Sign in again" is the only way back.
 *
 * JSX-free for the same reason as SignInScreen (see there).
 *
 * Props
 *   product   'ops' (Ops Centre, the default) | 'infinity' (24K Infinity)
 *   status    'signed-out' (default) | 'signing-out' (the logout redirect is on its way)
 *   context   short line above the title, e.g. "Tenant administration"
 *   onSignIn  called by "Sign in again"
 */
import React from 'react';
import { PRODUCTS, copyrightLine } from './SignInScreen';
import { ARKIS_WORDMARK, MARK_24K, SJ_SQUARE } from './signInMarks';
import './signInScreen.css';

/** The words of each state, kept apart from the markup so they can be tested. */
export function signedOutCopy(product = 'ops', status = 'signed-out') {
  const { name } = PRODUCTS[product] || PRODUCTS.ops;
  if (status === 'signing-out') {
    return {
      eyebrow: 'Signing out',
      title: 'Signing you out',
      text: `Ending your ${name} session…`,
      action: null,
    };
  }
  return {
    eyebrow: 'Signed out',
    title: 'You’re signed out',
    text: `You have signed out of ${name}. On a shared computer, close this browser window as well.`,
    action: 'Sign in again',
  };
}

const h = React.createElement;

function PoweredBy() {
  return h('div', { className: 'k24-signin-powered', 'aria-label': 'Powered by 24K · ARKIS ENGINE' },
    h('span', { 'aria-hidden': true }, 'Powered by'),
    h('img', { className: 'k24-signin-24k', src: MARK_24K, alt: '' }),
    h('span', { className: 'k24-signin-dot', 'aria-hidden': true }, '·'),
    h('span', { className: 'k24-signin-lockup', 'aria-hidden': true },
      h('img', { className: 'k24-signin-arkis', src: ARKIS_WORDMARK, alt: '' }),
      h('span', { className: 'k24-signin-engine' }, 'ENGINE')));
}

export default function SignedOutScreen({ product = 'ops', status = 'signed-out', context, onSignIn }) {
  const brand = PRODUCTS[product] || PRODUCTS.ops;
  const copy = signedOutCopy(product, status);
  const busy = status === 'signing-out';

  return h('div', { className: `k24-signin k24-signin--${product === 'infinity' ? 'infinity' : 'ops'}` },
    h('section', { className: 'k24-signin-form' },
      h('div', { className: 'k24-signin-grid', 'aria-hidden': true }),
      h('header', { className: 'k24-signin-head' },
        h('img', { className: 'k24-signin-wordmark', src: brand.wordmark, alt: brand.name }),
        h(PoweredBy)),
      h('main', { className: 'k24-signin-body', 'aria-live': 'polite', 'aria-busy': busy },
        h('div', { className: 'k24-signin-eyebrow' }, context && !busy ? context : copy.eyebrow),
        h('h1', null, copy.title),
        h('p', { className: 'k24-signin-text' },
          busy && h('span', { className: 'k24-signin-spinner', 'aria-hidden': true }),
          copy.text),
        copy.action ? h('button', { type: 'button', className: 'k24-signin-button', onClick: onSignIn }, copy.action) : null),
      h('footer', { className: 'k24-signin-foot' }, copyrightLine())),
    h('section', { className: 'k24-signin-show', 'aria-hidden': true },
      brand.slides.map((slide, i) => h('div', { key: `s${i}`, className: 'k24-signin-slide', style: { '--d': `${i * 7}s` } },
        h('img', { src: slide.image, alt: '', style: slide.position ? { objectPosition: slide.position } : undefined }))),
      h('div', { className: 'k24-signin-tint' }),
      h('div', { className: 'k24-signin-shade' }),
      h('img', { className: 'k24-signin-sj', src: SJ_SQUARE, alt: '' }),
      h('div', { className: 'k24-signin-caps' },
        brand.slides.map((slide, i) => h('div', { key: `c${i}`, className: 'k24-signin-cap', style: { '--d': `${i * 7}s` } },
          h('div', { className: 'k24-signin-eyebrow' }, slide.eyebrow),
          h('h2', null, slide.title),
          h('p', null, slide.text))),
        h('div', { className: 'k24-signin-dots' },
          brand.slides.map((slide, i) => h('span', { key: `d${i}`, style: { '--d': `${i * 7}s` } }))))));
}
